import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { useEffect, useState } from "react";
import axios from "axios";
import { Toaster } from "sonner";
import { API } from "@/lib/api";
import AnotherRelease from "@/pages/AnotherRelease";
import ManagePreferences from "@/pages/ManagePreferences";
import SmartLinkRedirect from "@/pages/SmartLinkRedirect";
import AccessGateway from "@/pages/AccessGateway";
import { AdminAuthProvider, useAdminAuth } from "@/admin/AdminAuth";
import AdminLogin from "@/admin/AdminLogin";
import AdminProjects from "@/admin/AdminProjects";
import AdminProjectEditor from "@/admin/AdminProjectEditor";
import ProjectShell from "@/admin/ProjectShell";
import Analytics from "@/admin/Analytics";
import Fans from "@/admin/Fans";
import OptIns from "@/admin/OptIns";
import Broadcast from "@/admin/Broadcast";
import TriggerView from "@/pages/TriggerView";
import { I18nProvider } from "@/i18n";
import { Loader2 } from "lucide-react";

function ProtectedAdmin({ children }) {
  const { user } = useAdminAuth();
  if (user === null) {
    return (
      <div className="min-h-[100dvh] bg-[#050505] flex items-center justify-center text-[#737373]">
        <Loader2 className="w-5 h-5 animate-spin" />
      </div>
    );
  }
  if (user === false) return <Navigate to="/admin/login" replace />;
  return children;
}

function RootResolver() {
  const [target, setTarget] = useState(null);
  useEffect(() => {
    let alive = true;
    const host = window.location.hostname;
    axios
      .get(`${API}/domain/resolve`, { params: { host } })
      .then((r) => { if (alive) setTarget(r.data?.slug ? `/${r.data.slug}` : "/another"); })
      .catch(() => { if (alive) setTarget("/another"); });
    return () => { alive = false; };
  }, []);
  if (!target) {
    return (
      <div className="min-h-[100dvh] bg-[#050505] flex items-center justify-center text-[#737373]">
        <Loader2 className="w-5 h-5 animate-spin" />
      </div>
    );
  }
  return <Navigate to={target} replace />;
}

function App() {
  return (
    <div className="App grain">
      <Toaster theme="dark" position="top-center" />
      <BrowserRouter>
        <I18nProvider>
        <AdminAuthProvider>
          <Routes>
            <Route path="/" element={<RootResolver />} />

            <Route path="/admin" element={<Navigate to="/admin/projects" replace />} />
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/admin/projects" element={<ProtectedAdmin><AdminProjects /></ProtectedAdmin>} />
            <Route path="/admin/projects/:slug" element={<ProtectedAdmin><ProjectShell /></ProtectedAdmin>}>
              <Route index element={<Navigate to="release" replace />} />
              <Route path="release" element={<AdminProjectEditor tab="release" />} />
              <Route path="audio" element={<AdminProjectEditor tab="audio" />} />
              <Route path="sections" element={<AdminProjectEditor tab="sections" />} />
              <Route path="trigger" element={<AdminProjectEditor tab="trigger" />} />
              <Route path="analytics" element={<Analytics />} />
              <Route path="fans" element={<Fans />} />
              <Route path="opt-ins" element={<OptIns />} />
              <Route path="broadcast" element={<Broadcast />} />
            </Route>

            <Route path="/trigger/:slug" element={<TriggerView />} />

            <Route path="/t/:code" element={<SmartLinkRedirect />} />
            <Route path="/:slug" element={<AnotherRelease />} />
            <Route path="/:slug/access" element={<AccessGateway />} />
            <Route path="/:slug/verify" element={<AccessGateway />} />
            <Route path="/:slug/preferences" element={<ManagePreferences />} />

            <Route path="*" element={<Navigate to="/another" replace />} />
          </Routes>
        </AdminAuthProvider>
        </I18nProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
